import { useState, type ChangeEvent } from 'react'
import { Box, Flex, VStack, Textarea } from '@chakra-ui/react'
import { LuCornerDownRight as CornerDownRight, LuSend as Send } from 'react-icons/lu'
import { Text, useT } from '@minetrace/ui'
import { useFormColors } from '@minetrace/ui/forms'
import { formatDateTime } from '../lib/dates'

export interface ObservationReplyItem {
  id: string
  replyText: string
  createdByUserName: string | null
  createdAt: string
}

interface ObservationRepliesProps {
  replies: ObservationReplyItem[]
  canReply: boolean
  onReply: (text: string) => Promise<void>
}

/**
 * Hilo de respuestas de una observación (estilo comentarios de PR). Muestra las
 * respuestas existentes y, si canReply, un campo para agregar una nueva. Lo usan
 * el evaluador y el registrador para dialogar sin salir del sistema.
 */
export default function ObservationReplies({ replies, canReply, onReply }: ObservationRepliesProps) {
  const c = useFormColors()
  const { t } = useT()
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)

  const handleSend = async () => {
    if (!text.trim() || sending) return
    setSending(true)
    try {
      await onReply(text.trim())
      setText('')
    } finally {
      setSending(false)
    }
  }

  return (
    <Box mt={replies.length > 0 || canReply ? 3 : 0} pl={3} borderLeft="2px solid" borderColor={c.border}>
      {replies.length > 0 && (
        <VStack gap={2} alignItems="stretch" mb={canReply ? 3 : 0}>
          {replies.map((r) => (
            <Box key={r.id}>
              <Flex alignItems="center" gap={1.5} mb={0.5}>
                <CornerDownRight size={12} color={c.textMuted} />
                <Text fontSize="xs" fontWeight="700" color={c.text}>{r.createdByUserName ?? t('shared.observations.defaultUser', { defaultValue: 'Usuario' })}</Text>
                <Text fontSize="xs" color={c.textMuted}>· {formatDateTime(r.createdAt)}</Text>
              </Flex>
              <Text fontSize="sm" color={c.text} pl={5}>{r.replyText}</Text>
            </Box>
          ))}
        </VStack>
      )}

      {canReply && (
        <Flex gap={2} alignItems="flex-end">
          <Textarea
            value={text}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
            placeholder={t('transportistas.observations.replyPlaceholder')}
            rows={1}
            fontSize="sm"
            bg={c.bgInput}
            border="1px solid"
            borderColor={c.border}
            borderRadius="lg"
            color={c.text}
            _placeholder={{ color: c.textMuted }}
            _focus={{ outline: 'none', borderColor: c.accent, boxShadow: `0 0 0 1px ${c.accent}` }}
          />
          <Box
            as="button"
            type="button"
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            px={4}
            h="38px"
            flexShrink={0}
            bg={!text.trim() || sending ? '#b5aa9d' : c.accent}
            color="white"
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            cursor={!text.trim() || sending ? 'not-allowed' : 'pointer'}
            _hover={!text.trim() || sending ? {} : { opacity: 0.9 }}
            onClick={handleSend}
          >
            <Send size={14} />
            {t('transportistas.observations.reply')}
          </Box>
        </Flex>
      )}
    </Box>
  )
}
